import {toSubscribableCancelToken} from '../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {
  ApplicationContext,
  ApplicationContextBase,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {serializeTeammateForApplication} from '../../../helpers/models/applicationSerializationHelpers';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {Dispatch} from '../../../store/appStore';
import {applicationOpenConversationInPopupAction} from '../../../store/data/applications/applicationConversationsActions';
import {
  authenticateApplicationAsyncAction,
  deauthenticateApplicationAsyncAction,
  fetchApplicationPathAction,
  relayApplicationHttpRequestAsyncAction,
} from '../../../store/data/applications/applicationsActions';
import {
  applicationOpenUrlAction,
  applicationOpenUrlInPopupAction,
} from '../../../store/data/applications/applicationsBrowserActions';
import {applicationListChannelsAction} from '../../../store/data/applications/applicationsChannelsActions';
import {applicationListInboxesAction} from '../../../store/data/applications/applicationsInboxesActions';
import {
  applicationComposeMessageAsyncAction,
  applicationUpdateDraftAction,
} from '../../../store/data/applications/applicationsMessagesActions';
import {applicationListTagsAsyncAction} from '../../../store/data/applications/applicationsTagsActions';
import {applicationListTeammatesAction} from '../../../store/data/applications/applicationsTeammateActions';
import {applicationSearchAction} from '../../../store/data/applications/legacyApplicationsActions';
import {NewPanelId} from '../../../store/panels/panelStateBase';

interface ContextBuilderParameters {
  panelId: NewPanelId;
  dispatch: Dispatch;
  user: TeammateModel;
  applicationId: number;
  preferences: object;
  authentication?: ApplicationEntryPointModel['authentication'];
  origin?: string;
}

export type ContextBuilder = (baseContext: ApplicationContextBase, dispatch: Dispatch) => ApplicationContext;

export function buildBaseContext(parameters: ContextBuilderParameters): ApplicationContextBase {
  const {dispatch, applicationId, panelId} = parameters;
  const {user, preferences, origin, authentication} = parameters;

  return {
    // The id of the context needs to be set to a random uuid when the entire context
    // is build
    id: 'id',

    // The hostId and entryPointId are not specifically needed for the context (they were used by
    // ui-sdk) but they are properties of the ApplicationContext and need to be
    // specificed for typescript compliance.
    hostId: 'hostId',
    entryPointId: 'entryPointId',

    functionArities: {}, // Will be filled dynamically right before sending the context.
    preferences,
    authentication: authentication || {},

    teammate: serializeTeammateForApplication(user),

    // We do need to support creating and destroying widgets as these were needed for the
    // ui-sdk. However they need to be added for typescript compliance.
    createWidget: async (template) => {
      const error = new Error(`The application ${applicationId} does not support creating widgets.`);
      reportToBugsnag(error);
      throw error;
    },

    destroyWidget: async (widgetId) => {
      const error = new Error(`The application ${applicationId} does not support destroying widgets.`);
      reportToBugsnag(error);
      throw error;
    },

    // The method sendHttp is currently not in scope. For now we will throw an
    // error and report it to Bugsnag.

    sendHttp: (request, cancelToken) => {
      const error = new Error(`The application ${applicationId} does not support forwarding HTTP requests.`);
      reportToBugsnag(error);
      throw error;
    },

    relayHttp: async (request, cancelToken) =>
      dispatch(
        relayApplicationHttpRequestAsyncAction(
          applicationId,
          request,
          toSubscribableCancelToken(cancelToken),
        ),
      ),

    authenticate: async () => dispatch(authenticateApplicationAsyncAction(applicationId, origin)),

    deauthenticate: async () => dispatch(deauthenticateApplicationAsyncAction(applicationId, origin)),

    search: async (query) => {
      dispatch(applicationSearchAction(query));
    },

    listTeammates: async (paginationToken) => dispatch(applicationListTeammatesAction(paginationToken)),

    listInboxes: async (paginationToken) => dispatch(applicationListInboxesAction(paginationToken)),

    listChannels: async (paginationToken) => dispatch(applicationListChannelsAction(paginationToken)),

    listTags: (paginationToken) => dispatch(applicationListTagsAsyncAction(paginationToken)),

    openUrl: async (url) => dispatch(applicationOpenUrlAction(applicationId, url)),

    openUrlInPopup: async (url, options) =>
      dispatch(applicationOpenUrlInPopupAction(applicationId, url, options)),

    openConversationInPopup: async (conversationId) => {
      const parsedConversationId = tryParsePublicId(conversationId);
      if (!parsedConversationId) {
        throw new Error('Unable to parse conversation id.');
      }
      return dispatch(applicationOpenConversationInPopupAction(parsedConversationId));
    },

    createDraft: async (draftTemplate) => {
      if (draftTemplate.replyOptions) {
        throw new Error('Unable to create a reply draft from this context.');
      }

      return dispatch(applicationComposeMessageAsyncAction(draftTemplate));
    },

    updateDraft: async (draftId, update) => {
      await dispatch(applicationUpdateDraftAction(draftId, update));
    },

    fetchPath: async () => dispatch(fetchApplicationPathAction(panelId)),
  };
}
